// src/components/resumo/quiz/QuizReadManager.ts
// 🎯 Dados de quiz em modo leitura + utilitário para separar o HTML salvo em
// pedaços de HTML "normal" e marcadores de quiz.
//
// Histórico: as duas primeiras versões tentavam achar os marcadores de quiz
// DEPOIS que o React já tinha desenhado o conteúdo (mutando o DOM ao vivo, ou
// com um MutationObserver reagindo a mudanças). As duas formas entraram em
// loop com o próprio React re-renderizando aquele bloco (a página piscava e,
// numa das tentativas, travou a aba inteira). A forma segura é nunca tocar o
// DOM depois de renderizado: aqui a gente quebra a STRING salva em pedaços —
// texto normal (vai para <HighlightedContent>) e marcadores de quiz (viram
// um <QuizInlineAccordion> normal, como filho comum do React, sem portal e
// sem re-escanear nada).

import { QuizBadgeData, QuizProgress } from './quiz.types';

const QUIZ_PROGRESS_PREFIX = 'graia_quiz_progress_';

/** Lê o progresso salvo de um quiz (sobrevive a navegação de página e reload). */
export const loadQuizProgress = (quizId: string): QuizProgress | null => {
    try {
        const raw = localStorage.getItem(QUIZ_PROGRESS_PREFIX + quizId);
        return raw ? JSON.parse(raw) : null;
    } catch {
        return null;
    }
};

/** Salva o progresso de um quiz. Falha silenciosamente se localStorage não estiver disponível. */
export const saveQuizProgress = (quizId: string, progress: QuizProgress): void => {
    try {
        localStorage.setItem(QUIZ_PROGRESS_PREFIX + quizId, JSON.stringify(progress));
    } catch {
        // localStorage indisponível (modo privado, quota cheia, etc.) — ignora
    }
};

export interface ContentSegment {
    type: 'html' | 'quiz';
    html?: string;
    quizId?: string;
}

/**
 * Divide o HTML salvo da página em uma sequência de pedaços de HTML comum e
 * marcadores de quiz (identificados por `data-quiz-id`), não importa em que
 * profundidade o marcador esteja aninhado (dentro de um parágrafo, tabela,
 * callout, etc.) — usa a Range API do próprio navegador para "cortar" a
 * árvore ao redor do marcador, que já sabe reconstruir as tags dos
 * ancestrais corretamente dos dois lados do corte.
 */
export const splitContentByQuiz = (html: string): ContentSegment[] => {
    if (!html || !html.includes('data-quiz-id')) {
        return [{ type: 'html', html: html || '' }];
    }

    const temp = document.createElement('div');
    temp.innerHTML = html;

    const markers = Array.from(temp.querySelectorAll('[data-quiz-id]')) as HTMLElement[];
    if (markers.length === 0) {
        return [{ type: 'html', html }];
    }

    const serialize = (fragment: DocumentFragment): string => {
        const holder = document.createElement('div');
        holder.appendChild(fragment);
        return holder.innerHTML;
    };

    const segments: ContentSegment[] = [];
    const cursor = document.createRange();
    cursor.setStart(temp, 0);
    cursor.collapse(true);

    markers.forEach((marker) => {
        if (!temp.contains(marker)) return; // marcador duplicado/inválido

        const beforeRange = document.createRange();
        beforeRange.setStart(cursor.startContainer, cursor.startOffset);
        beforeRange.setEndBefore(marker);
        const beforeHtml = serialize(beforeRange.cloneContents());
        if (beforeHtml.trim()) {
            segments.push({ type: 'html', html: beforeHtml });
        }

        const quizId = marker.getAttribute('data-quiz-id');
        if (quizId) {
            segments.push({ type: 'quiz', quizId });
        }

        cursor.setStartAfter(marker);
    });

    if (temp.childNodes.length > 0) {
        const afterRange = document.createRange();
        afterRange.setStart(cursor.startContainer, cursor.startOffset);
        afterRange.setEnd(temp, temp.childNodes.length);
        const afterHtml = serialize(afterRange.cloneContents());
        if (afterHtml.trim()) {
            segments.push({ type: 'html', html: afterHtml });
        }
    }

    return segments.length > 0 ? segments : [{ type: 'html', html }];
};

export class QuizReadManager {
    private quizDataStore: Map<string, QuizBadgeData>;

    constructor() {
        this.quizDataStore = new Map();
    }

    importQuizzes(quizzes: Record<string, QuizBadgeData>) {
        Object.entries(quizzes).forEach(([id, data]) => {
            this.quizDataStore.set(id, data);
        });
        console.log('✅ Quizzes importados:', Object.keys(quizzes).length);
    }

    getQuizData(quizId: string): QuizBadgeData | null {
        return this.quizDataStore.get(quizId) || null;
    }
}
